import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  FiSearch, FiMenu, FiX, FiShoppingCart, FiBell, FiGrid, 
  FiChevronDown, FiLogOut, FiSettings,
  FiBookOpen, FiUsers, FiShoppingBag, FiBriefcase, 
  FiCpu, FiLayers, FiActivity, FiGlobe,
  FiHome, FiUser, FiInfo, FiPhone, FiShield, FiFileText
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';

// ==================================================================================
// 1️⃣ DATA ARCHITECTURE
// ==================================================================================
const NAV_DATA = [
  {
    id: 'academy',
    label: 'الأكاديمية',
    icon: <FiBookOpen />,
    path: '/academy',
    columns: [
      {
        title: 'أقسام الكورسات',
        icon: <FiCpu className="text-blue-500" />,
        items: [
          { label: 'أنظمة مدمجة (Embedded)', path: '/courses?category=Embedded Systems' },
          { label: 'تطوير الويب (Web Dev)', path: '/courses?category=Web Development' },
          { label: 'ذكاء اصطناعي (AI)', path: '/courses?category=AI & Data' },
          { label: 'أتمتة صناعية (PLC)', path: '/courses?category=Automation' },
        ]
      },
      {
        title: 'نظام التعلم',
        icon: <FiLayers className="text-indigo-500" />,
        items: [
          { label: 'المسارات التعليمية', path: '/roadmaps' },
          { label: 'المعسكرات (Bootcamps)', path: '/bootcamps' },
          { label: 'دروس مباشرة (Live)', path: '/lessons/live' },
        ]
      },
      {
        title: 'المشاريع والشهادات',
        icon: <FiActivity className="text-teal-500" />,
        items: [
          { label: 'مشاريع التخرج', path: '/projects/graduation' },
          { label: 'الشهادات المعتمدة', path: '/certificates/verify' },
          { label: 'المعامل الافتراضية', path: '/labs' },
        ]
      }
    ]
  },
  {
    id: 'community',
    label: 'المجتمع',
    icon: <FiUsers />,
    path: '/community',
    columns: [
      {
        title: 'تواصل وتعلم',
        icon: <FiGlobe className="text-purple-500" />,
        items: [
          { label: 'المنتديات التقنية', path: '/community/forums' },
          { label: 'مجموعات الدراسة', path: '/community/groups' },
          { label: 'اسأل خبير', path: '/community/qa' },
        ]
      },
      {
        title: 'الفعاليات',
        icon: <FiActivity className="text-pink-500" />,
        items: [
          { label: 'الهاكاثون السنوي', path: '/events/hackathon' },
          { label: 'ورش عمل (Workshops)', path: '/events/workshops' },
          { label: 'مسابقات برمجية', path: '/events/contests' },
        ]
      }
    ]
  },
  {
    id: 'store',
    label: 'المتجر',
    icon: <FiShoppingBag />,
    path: '/store',
    columns: [
      {
        title: 'منتجاتنا',
        icon: <FiShoppingBag className="text-green-500" />,
        items: [
          { label: 'حقائب أردوينو', path: '/store/arduino-kits' },
          { label: 'مكونات إلكترونية', path: '/store/components' },
          { label: 'طابعات ثلاثية الأبعاد', path: '/store/3d-printers' },
        ]
      }
    ]
  },
  {
    id: 'career',
    label: 'المسار المهني',
    icon: <FiBriefcase />,
    path: '/career',
    columns: [
      {
        title: 'التوظيف',
        icon: <FiBriefcase className="text-orange-500" />,
        items: [
          { label: 'لوحة الوظائف', path: '/jobs' },
          { label: 'التدريب الصيفي', path: '/internships' },
          { label: 'بناء السيرة الذاتية', path: '/cv-builder' },
        ]
      }
    ]
  }
];

// ==================================================================================
// 2️⃣ MAIN COMPONENT
// ==================================================================================
const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);

  // --- STATE ---
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  // --- RESET UI ON NAVIGATE ---
  useEffect(() => {
    setMobileMenuOpen(false);
    setProfileMenuOpen(false);
    setActiveMegaMenu(null);
  }, [location]);

  // --- HIDE LOGIC (Optional: hide nav on certain pages) ---
  if (
    location.pathname.startsWith('/admin') || 
    location.pathname.startsWith('/instructor') || 
    location.pathname.includes('/learn')
  ) return null;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  return (
    <>
      {/* ==========================================================================
        🖥️ DESKTOP NAVBAR (Sticky & Glass)
        ==========================================================================
      */}
      <nav 
        className="sticky top-0 z-[100] w-full bg-white/90 dark:bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-gray-100 dark:border-gray-800 transition-colors duration-300"
        dir="rtl"
      >
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[72px]">
            
            {/* --- SECTION 1: LOGO & MEGA MENU --- */}
            <div className="flex items-center gap-8 h-full">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3 flex-shrink-0">
                <Logo className="h-9 w-auto" />
                <span className="font-bold text-2xl tracking-tighter text-gray-900 dark:text-white hidden xl:block">
                  iTaleb<span className="text-blue-600">Tech</span>
                </span>
              </Link>

              {/* Desktop Nav Links */}
              <div className="hidden lg:flex items-center gap-2 h-full">
                {NAV_DATA.map((item) => (
                  <div 
                    key={item.id}
                    className="relative h-full flex items-center"
                    onMouseEnter={() => setActiveMegaMenu(item.id)}
                    onMouseLeave={() => setActiveMegaMenu(null)}
                  >
                    <Link 
                      to={item.path}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold transition-all duration-200
                        ${activeMegaMenu === item.id 
                          ? 'text-blue-600 bg-blue-50 dark:bg-white/10 dark:text-blue-400' 
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'}
                      `}
                    >
                      {item.label}
                      <FiChevronDown className={`transition-transform duration-200 ${activeMegaMenu === item.id ? 'rotate-180' : ''}`} />
                    </Link>

                    {/* ✨ MEGA MENU PANEL ✨ */}
                    <AnimatePresence>
                      {activeMegaMenu === item.id && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full right-0 mt-1 w-[800px] bg-white dark:bg-[#111] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden z-50 ring-1 ring-black/5"
                        >
                          <div className="flex min-h-[300px]">
                            {/* Side Banner */}
                            <div className="w-1/4 bg-gray-50 dark:bg-[#1a1a1a] p-6 flex flex-col justify-between border-l border-gray-100 dark:border-gray-800">
                              <div>
                                <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2">{item.label}</h4>
                                <p className="text-xs text-gray-500 leading-relaxed">
                                  استكشف أحدث التقنيات والدورات التدريبية المتاحة في هذا القسم.
                                </p>
                              </div>
                              <div className="text-8xl text-blue-500/10 self-end">
                                {item.icon}
                              </div>
                            </div>

                            {/* Links Grid */}
                            <div className="w-3/4 p-8 grid grid-cols-2 gap-x-12 gap-y-8">
                              {item.columns.map((col, idx) => (
                                <div key={idx} className="group">
                                  <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-100 dark:border-gray-800">
                                    <div className="p-2 bg-gray-50 dark:bg-white/5 rounded-lg text-lg">
                                      {col.icon}
                                    </div>
                                    <h5 className="font-bold text-gray-900 dark:text-white text-sm">{col.title}</h5>
                                  </div>
                                  <ul className="space-y-2.5">
                                    {col.items.map((subItem, sIdx) => (
                                      <li key={sIdx}>
                                        <Link 
                                          to={subItem.path} 
                                          className="block text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:translate-x-[-4px] transition-all duration-200 font-medium"
                                        >
                                          {subItem.label}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>

            {/* --- SECTION 2: SEARCH BAR --- */}
            <div className="hidden xl:flex flex-1 max-w-lg px-8">
              <div className={`relative w-full group transition-all duration-300 ${searchFocused ? 'scale-105' : ''}`}>
                <div className="relative flex items-center bg-gray-100 dark:bg-[#1a1a1a] rounded-full h-11 border border-transparent focus-within:border-blue-500 focus-within:bg-white dark:focus-within:bg-black focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
                  <input 
                    ref={searchInputRef}
                    type="text" 
                    placeholder="ابحث عن كورس، مسار، أو مقال..." 
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    className="w-full h-full px-6 bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400 text-sm font-medium"
                  />
                  <button className="absolute left-1.5 h-8 w-8 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 rounded-full flex items-center justify-center hover:text-blue-600 transition-colors shadow-sm">
                    <FiSearch size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* --- SECTION 3: USER ACTIONS --- */}
            <div className="flex items-center gap-3">
              <ThemeToggle />

              {user ? (
                <>
                  {/* Notifications */}
                  <div className="relative">
                    <button className="p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors">
                      <FiBell size={20} />
                      <span className="absolute top-2 right-2.5 w-2 h-2 bg-red-500 border-2 border-white dark:border-black rounded-full"></span>
                    </button>
                  </div>

                  {/* Cart (Hidden on Mobile) */}
                  <Link to="/cart" className="relative p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors hidden sm:block">
                    <FiShoppingCart size={20} />
                    <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[10px] font-bold h-4 w-4 flex items-center justify-center rounded-full border border-white dark:border-black">2</span>
                  </Link>

                  {/* 👤 PROFILE DROPDOWN */}
                  <div className="relative" onMouseEnter={() => setProfileMenuOpen(true)} onMouseLeave={() => setProfileMenuOpen(false)}>
                    <button className="flex items-center gap-2 pl-1 pr-1 py-1 rounded-full border border-transparent hover:bg-gray-100 dark:hover:bg-white/10 transition-all">
                      <div className="h-9 w-9 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700 ring-2 ring-transparent group-hover:ring-blue-500/20">
                         {user.photoURL ? (
                           <img 
                             src={user.photoURL} 
                             alt="Profile" 
                             className="h-full w-full object-cover" 
                             referrerPolicy="no-referrer"
                           />
                         ) : (
                           <div className="h-full w-full bg-gradient-to-tr from-blue-600 to-blue-400 flex items-center justify-center text-white font-bold text-sm">
                             {user.firstName?.[0]}
                           </div>
                         )}
                      </div>
                      <FiChevronDown className="text-gray-400 text-xs hidden xl:block" />
                    </button>

                    <AnimatePresence>
                      {profileMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-[#111] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden z-50"
                        >
                          <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-black/20 flex items-center gap-4">
                            <div className="h-12 w-12 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700">
                               {user.photoURL ? (
                                 <img src={user.photoURL} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                               ) : (
                                 <div className="h-full w-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">{user.firstName?.[0]}</div>
                               )}
                            </div>
                            <div className="flex-1 min-w-0">
                               <p className="font-bold text-gray-900 dark:text-white truncate text-sm">{user.firstName} {user.lastName}</p>
                               <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                          </div>

                          <div className="p-2 space-y-1">
                            {/* ✅ ADDED PROFILE LINK HERE */}
                            <Link to="/profile" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-blue-600 transition-colors">
                              <FiUser /> الملف الشخصي
                            </Link>

                            <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-blue-600 transition-colors">
                              <FiGrid /> لوحة التحكم
                            </Link>
                            
                            <Link to="/settings" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-blue-600 transition-colors">
                              <FiSettings /> الإعدادات
                            </Link>
                            
                            <div className="h-px bg-gray-100 dark:bg-gray-800 my-1 mx-2" />
                            
                            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                              <FiLogOut /> تسجيل خروج
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" className="hidden lg:block px-5 py-2 text-sm font-bold text-gray-700 dark:text-white hover:text-blue-600 transition-colors">
                    دخول
                  </Link>
                  <Link to="/signup" className="px-6 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold shadow-lg shadow-blue-500/30 transition-all hover:scale-105">
                    ابدأ مجاناً
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ==========================================================================
        📱 MOBILE BOTTOM BAR (The App Experience)
        ==========================================================================
      */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[90] bg-white dark:bg-[#0a0a0a] border-t border-gray-200 dark:border-gray-800 pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
        <div className="grid grid-cols-5 h-[65px] items-center px-2 relative">
          
          <MobileNavLink to="/" icon={<FiHome size={22} />} label="الرئيسية" active={location.pathname === '/'} />
          <MobileNavLink to="/academy" icon={<FiBookOpen size={22} />} label="الأكاديمية" active={location.pathname.startsWith('/academy')} />
          
          {/* Floating Center Button - Perfectly Centered */}
          <div className="relative -top-6 flex justify-center items-center w-full">
            <Link 
              to={user ? "/dashboard" : "/signup"}
              className="w-14 h-14 rounded-full bg-gradient-to-tr from-blue-600 to-blue-500 text-white flex items-center justify-center shadow-xl shadow-blue-600/30 border-4 border-white dark:border-[#0a0a0a] transform active:scale-95 transition-transform"
            >
              {user ? <FiGrid size={24} /> : <FiUser size={24} />}
            </Link>
          </div>

          <MobileNavLink to="/community" icon={<FiUsers size={22} />} label="المجتمع" active={location.pathname.startsWith('/community')} />
          
          {/* The "More" Trigger */}
          <button 
            onClick={() => setMobileMenuOpen(true)}
            className={`flex flex-col items-center justify-center gap-1 w-full h-full ${mobileMenuOpen ? 'text-blue-600' : 'text-gray-400 dark:text-gray-500'}`}
          >
            <FiMenu size={22} />
            <span className="text-[10px] font-medium">المزيد</span>
          </button>

        </div>
      </div>

      {/* ==========================================================================
        📱 MOBILE DRAWER (Sidebar + Footer Links)
        ==========================================================================
      */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] lg:hidden"
            />
            
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-[85%] max-w-[320px] bg-white dark:bg-[#0f0f0f] z-[101] lg:hidden flex flex-col shadow-2xl"
              dir="rtl"
            >
              {/* Header */}
              <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-black/20">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">القائمة الرئيسية</h2>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 bg-gray-100 dark:bg-white/10 rounded-full">
                  <FiX size={20} className="text-gray-600 dark:text-white" />
                </button>
              </div>

              {/* Scroll Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                <div className="relative">
                  <input type="text" placeholder="بحث..." className="w-full py-3 px-4 bg-gray-100 dark:bg-white/5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:text-white" />
                  <FiSearch className="absolute left-4 top-3.5 text-gray-400" />
                </div>

                <div className="space-y-4">
                  {NAV_DATA.map((item) => (
                    <div key={item.id} className="border-b border-gray-50 dark:border-gray-800/50 pb-4 last:border-0">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-lg text-blue-600">{item.icon}</span>
                        <h3 className="font-bold text-gray-800 dark:text-white">{item.label}</h3>
                      </div>
                      <div className="pr-11 space-y-3">
                        {item.columns.flatMap(col => col.items).slice(0, 4).map((sub, i) => (
                          <Link 
                            key={i} 
                            to={sub.path} 
                            onClick={() => setMobileMenuOpen(false)}
                            className="block text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-cyan-400"
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {!user && (
                  <div className="grid grid-cols-2 gap-3 pt-4">
                    <Link to="/login" className="py-3 text-center rounded-xl font-bold bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white">دخول</Link>
                    <Link to="/signup" className="py-3 text-center rounded-xl font-bold bg-blue-600 text-white">تسجيل</Link>
                  </div>
                )}
              </div>

              {/* Drawer Footer (Replaces Main Footer on Mobile) */}
              <div className="p-5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-black/20">
                <div className="grid grid-cols-2 gap-y-3 mb-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                   <Link to="/about" className="hover:text-blue-600 flex items-center gap-1"><FiInfo /> من نحن</Link>
                   <Link to="/contact" className="hover:text-blue-600 flex items-center gap-1"><FiPhone /> اتصل بنا</Link>
                   <Link to="/privacy" className="hover:text-blue-600 flex items-center gap-1"><FiShield /> السياسة</Link>
                   <Link to="/terms" className="hover:text-blue-600 flex items-center gap-1"><FiFileText /> الشروط</Link>
                </div>

                <ThemeToggle className="w-full justify-center py-2 bg-white dark:bg-white/5 rounded-lg border border-gray-200 dark:border-transparent mb-3" />
                
                {user && (
                  <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 text-red-600 font-bold text-sm">
                    <FiLogOut /> تسجيل خروج
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Helper for Mobile Bottom Nav
const MobileNavLink = ({ to, icon, label, active }) => (
  <Link 
    to={to} 
    className={`flex flex-col items-center justify-center gap-1 w-full h-full transition-colors ${
      active ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
    }`}
  >
    {React.cloneElement(icon, { size: 22 })}
    <span className="text-[10px] font-medium">{label}</span>
  </Link>
);

export default Navbar;